import type {
  ResolutionString,
  Bar,
  LibrarySymbolInfo,
  PeriodParams,
  HistoryCallback,
  DatafeedErrorCallback,
} from 'charting_library';
import { MarketDataCandles } from '@/api/spec.ts';
import { environment } from '../../../environment.ts';
import { DexIntervalMapper } from './DexIntervalMapper';
import type { CandleSeries } from '@/api/spec.ts';
import { mapCandleSeriesToBars } from './mapCandleSeriesToBars.ts';
import { queryClient } from '../../../main.tsx';
import { captureError } from '../../../utils/sentryHelpers';

const MAX_CANDLES_PER_REQUEST = 1024;
// 8 × 1024 bars ≈ 5.7 days of 1m / 28 days of 5m per TV request — beyond any
// realistic saved visible range.
const MAX_REQUESTS_PER_CALL = 8;

export class DexHistoricalDataProvider {
  private historyStart = Date.UTC(2025, 0, 1) / 1000;
  private intervalMapper = new DexIntervalMapper();

  async getBars(
    symbolInfo: LibrarySymbolInfo,
    resolution: ResolutionString,
    periodParams: PeriodParams,
    onHistoryCallback: HistoryCallback,
    onErrorCallback: DatafeedErrorCallback
  ): Promise<void> {
    const marketId = symbolInfo.ticker;
    const { from, to, countBack } = periodParams;

    if (!marketId || to < this.historyStart) {
      onHistoryCallback([], { noData: true });
      return;
    }

    const exchangeInterval = this.intervalMapper.getExchangeInterval(resolution);

    if (!exchangeInterval) {
      onErrorCallback('Unsupported resolution');
      return;
    }

    // TV paginates with `to = its own previous from` and never re-requests a range it
    // believes was answered, so the window must be covered completely — a partial
    // response rots into a permanent whitespace hole on the chart. Per the datafeed
    // contract `countBack` is a hard minimum and `from` a soft hint: honor the wider.
    // Bar-align both ends; alignedTo (not raw `to`) keeps the bar containing a
    // non-aligned `to` in range (countBack=1 scroll-back edge).
    const intervalSeconds = parseInt(exchangeInterval, 10);
    const alignedFrom = Math.floor(from / intervalSeconds) * intervalSeconds;
    const alignedTo = Math.floor(to / intervalSeconds) * intervalSeconds;
    const spanBars = Math.floor((alignedTo - alignedFrom) / intervalSeconds) + 1;
    const requiredBars = Math.max(countBack || 0, spanBars, 1);
    const requestFrom = Math.max(alignedTo - (requiredBars - 1) * intervalSeconds, this.historyStart);
    const totalBars = Math.floor((alignedTo - requestFrom) / intervalSeconds) + 1;

    const chunkCount = Math.min(Math.ceil(totalBars / MAX_CANDLES_PER_REQUEST), MAX_REQUESTS_PER_CALL);
    if (totalBars > chunkCount * MAX_CANDLES_PER_REQUEST) {
      console.warn('Candle window exceeds chunk budget, oldest part skipped', { marketId, resolution, totalBars });
    }

    // Adjacent, non-overlapping chunks, newest first; chunk 0 keeps the raw `to` so
    // the in-progress bar stays included.
    const chunks = Array.from({ length: chunkCount }, (_, i) => {
      const newest = alignedTo - i * MAX_CANDLES_PER_REQUEST * intervalSeconds;
      return {
        from: Math.max(newest - (MAX_CANDLES_PER_REQUEST - 1) * intervalSeconds, requestFrom),
        to: i === 0 ? to : newest,
      };
    });

    try {
      const seriesList = await Promise.all(
        chunks.map((chunk) => {
          const query = MarketDataCandles.replace(':market_id', marketId)
            .replace(':resolution', exchangeInterval)
            .replace(':from', (chunk.from * 1000).toString())
            .replace(':to', (chunk.to * 1000).toString());
          const url = `${environment.apiUrl}${query}`;

          return queryClient.fetchQuery<CandleSeries>({
            queryKey: ['candles', marketId, resolution, chunk.from, chunk.to],
            queryFn: async () => {
              const response = await fetch(url);
              if (!response.ok) {
                const error: Error & { response?: { status: number } } = new Error(
                  `HTTP ${response.status}: ${response.statusText}`
                );
                error.response = { status: response.status };
                throw error;
              }
              return response.json();
            },
            staleTime: 0,
          });
        })
      );

      const barsByTime = new Map<number, Bar>();
      seriesList.forEach((candleSeries) => {
        mapCandleSeriesToBars(candleSeries, marketId).forEach((bar) => {
          if (bar.time >= requestFrom * 1000 && bar.time <= to * 1000) {
            barsByTime.set(bar.time, bar);
          }
        });
      });
      const filteredBars = Array.from(barsByTime.values()).sort((a, b) => a.time - b.time);

      if (filteredBars.length === 0) {
        onHistoryCallback([], { noData: true });
        return;
      }

      onHistoryCallback(filteredBars, { noData: false });
    } catch (error) {
      console.error('Error fetching DEX historical data:', error);
      captureError(error, 'dex_historical_data_fetch');
      onErrorCallback(error instanceof Error ? error.message : 'Unknown error');
    }
  }
}
